import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-offers-mfe';
import { DebugService } from '../../shared/services/debug.service.js';
import { buttons } from '../../shared/styles/buttons.style.js';
import { style } from './choose-iconic-offer.style.js';
import { gravityGrid, gravityContainer, gravitySpacing, gravityTypography } from '../../shared/styles/gravity.style.js';
import { AppService } from '../../app.service.js';
import { links } from '../../shared/styles/links.style.js';
import { stratum } from '../../../constants/stratum.js';
import { OffersService } from '../offers.service.js';

class RefiIconicChooseOffer extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicChooseOffer');
    this.appExpDate = AppService.appExpDate;
    this.currentLoan = OffersService.currentLoan;
    this.offers = OffersService.offers;
    this.isMarketedOffer = this.offers.length > 0 ? this.offers[0].marketedOffer : false;
    this.agentGeneratedOffer = OffersService.agentGeneratedOffer;
    this.showAprInterstitial = false;
    this.firedPageView = false;
    if (this.offers.includes(this.agentGeneratedOffer)) {
      this.agentGeneratedOffer = null;
    }
  }

  static get styles() {
    return [buttons, links, gravityGrid, gravityContainer, gravitySpacing, style, gravityTypography];
  }

  static get properties() {
    return {
      noPositiveOffers: { type: Boolean, attribute: 'no-positive-offers' },
      noPositiveMonthlyOffers: { type: Boolean, attribute: 'no-positive-monthly-offers' },
      preQual: { type: Object, attribute: true },
      currentLoan: { type: Object, attribute: true },
      firstPossibleOffer: { type: Object, attribute: true },
      offers: { type: Array, attribute: true },
      hasStips: { type: Boolean, attribute: true },
      hasSavings: { type: Boolean, attribute: true },
      showAprInterstitial: { type: Boolean, attribute: true },
    };
  }

  get headerText() {
    if (this.isMarketedOffer) {
      return html` <h1 style="margin:0;" class="grv-text--large-1 grv-margin--medium-1 grv-weight--semibold  grv-color--white">Compare and select your new loan</h1>`;
    }
    return html`
      <h1 style="margin:0;" class="grv-text--large-1 grv-margin--medium-1 grv-weight--semibold  grv-color--white">You have offers!</h1>
      <p style="margin: 0;" class="grv-text--medium-2 grv-margin--medium-1 grv-weight--semibold grv-color--white">Compare and select your new loan</p>
    `;
  }

  async connectedCallback() {
    super.connectedCallback();
    const seenAprInterstitial = sessionStorage.getItem('seenAprInterstitial');
    if (this.isMarketedOffer && !seenAprInterstitial) {
      this.showAprInterstitial = true;
    }
    window.addEventListener('scroll', () => {
      const el = this.shadowRoot.querySelector('.original-loan-section');
      const stickyTop = parseInt(window.getComputedStyle(el).top, 10);
      const currentTop = el.getBoundingClientRect().top;
      el.classList.toggle('is-unstuck', currentTop === stickyTop);
    });

    document.dispatchEvent(
      new CustomEvent('header-bottom-slot-color-change', {
        bubbles: true,
        composed: true,
        detail: { colored: true },
      }),
    );
  }

  disconnectedCallback() {
    document.dispatchEvent(
      new CustomEvent('header-bottom-slot-color-change', {
        bubbles: true,
        composed: true,
        detail: { colored: false },
      }),
    );
  }

  get agentGeneratedOfferCardUX() {
    if (!this.agentGeneratedOffer) {
      return '';
    }
    return html` <refi-iconic-offer-card
      .offer="${this.agentGeneratedOffer}"
      .customerDetail="${this.customerDetail}"
      .currentLoan="${this.currentLoan}"
      .hasSavings="${this.hasSavings}"
      .hasStips="${this.hasStips}"
      loan-action="${this.isBureauPrefill ? 'selected' : 'entered'}"
      ?show-fastpath="${this.useFastPath && !this.hasStips}"
      data-e2e-ref="iconicOfferCard0"
    ></refi-iconic-offer-card>`;
  }

  get selectOfferCardsUX() {
    const gen = (function* generator(index) {
      for (let value = 1; value <= index; value += 1) {
        yield value;
      }
    })(this.offers.length);

    const mapper = offer => html` <refi-iconic-offer-card
      .offer="${offer}"
      .customerDetail="${this.customerDetail}"
      .currentLoan="${this.currentLoan}"
      .isSingleOffer="${this.offers.length === 1}"
      .hasSavings="${this.hasSavings}"
      .hasStips="${this.hasStips}"
      loan-action="${this.isBureauPrefill ? 'selected' : 'entered'}"
      ?show-fastpath="${this.useFastPath && !this.hasStips}"
      data-e2e-ref="iconicOfferCard${gen.next().value}"
    ></refi-iconic-offer-card>`;

    return this.offers.map(offer => html`${mapper(offer)}`);
  }

  get footerContent() {
    return html`
      <div class="default-disclosure">
        <div class="disclosure-text">
          *If you are currently delinquent on the auto loan you are seeking to refinance or if the payoff amount we verify with your current lender exceeds our limits, you will be required to become current and/or pay down the balance in order to
          move forward with a Capital One auto refinance. If you have questions about your existing auto loan, including whether you are delinquent, please contact your current lender.
        </div>

        <div class="disclosure-text">Your original loan information shown is based on the TransUnion data found or the loan information you provided.</div>
      </div>
      <div class="date-valid-through">
        Your pre-qualification is valid through <br />
        ${this.appExpDate} CST.
      </div>
    `;
  }

  render() {
    this.debug.log('rendered');
    if (this.showAprInterstitial) {
      return html` <refi-approved-apr @show-offers=${this.showOffers}></refi-approved-apr>`;
    }
    if (!this.firedPageView) {
      stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_OFFERS);
      this.firedPageView = true;
    }
    return html` <refi-track-pageview pagename="choose iconic offer"></refi-track-pageview>
      <div class="offers-list-compare-old-loan">
        <div class="header-and-body">
          <div class="header-section">
            <div class="header-content">${this.headerText}</div>
          </div>
          <div class="original-loan-section">
            <div class="original-loan">
              <div class="title">Your original loan</div>
              <refi-iconic-offer-card-details
                monthly-payment="${this.currentLoan.currentMonthlyPayment}"
                offer-apr="${this.currentLoan.apr}"
                term="${this.currentLoan.remainingPayment}"
                ui-gravity="true"
                payment-size="grv-text--medium-2"
                gap-class="card-details-dynamic"
              >
              </refi-iconic-offer-card-details>
            </div>
          </div>
          <div class="cards-section _gb-show">${this.testHeader || ''} ${this.agentGeneratedOfferCardUX || ''} ${this.selectOfferCardsUX}</div>
        </div>
        <div class="footer-section">${this.footerContent}</div>
      </div>`;
  }

  showOffers() {
    this.showAprInterstitial = false;
    sessionStorage.setItem('seenAprInterstitial', 'true');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

customElements.define('refi-iconic-choose-offer', RefiIconicChooseOffer);
